import { useCallback, useRef, useState } from 'react';
import type { ElementNodeDto } from '@xshop/shared';
import { BundleContext, BundleHotspotsContext, type BundleChoice } from './bundleContext';
import { useCommerce } from '../../commerce/ShopCommerce';
import { orderableVariant } from '../../commerce/pendingLine';
import { isThemePreview } from '../../api/previewVisit';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { textProp } from './elementProps';
import { buyButton } from './usePurchase';
import './elementBundle.css';

export const BundleElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { editing } = useElementRender();
  const locked = editing || isThemePreview();
  const commerce = useCommerce();
  const root = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<string | null>(null);
  const [entries, setEntries] = useState<Record<string, BundleChoice>>({});
  const register = useCallback((id: string, choice: BundleChoice | null) => setEntries((prior) => {
    const next = { ...prior }; if (choice) next[id] = choice; else delete next[id]; return next;
  }), []);
  const scopes = (node: ElementNodeDto): ElementNodeDto[] => node.type === 'product-scope' ? [node] : node.children.flatMap(scopes);
  const products = scopes(node);
  const expected = products.length;
  const choices = products.flatMap((item) => entries[item.id] ? [entries[item.id]] : []);
  const available = expected > 0 && choices.length === expected && choices.every(({ product, variantId, quantity }) => orderableVariant(product, { slug: product.slug, variantId, quantity }));
  const position = (value: unknown, fallback: number) => typeof value === 'number' && Number.isFinite(value) ? Math.max(5, Math.min(95, value)) : fallback;
  const hotspots = {
    items: products.map((item, index) => ({ id: item.id, title: entries[item.id]?.product.title ?? `Product ${index + 1}`, x: position(item.props.hotspotX, 25 + (index % 3) * 25), y: position(item.props.hotspotY, 40 + (index % 2) * 20) })),
    active,
    select: (id: string) => {
      setActive(id);
      const target = Array.from(root.current?.querySelectorAll<HTMLElement>('[data-bundle-product]') ?? []).find((element) => element.dataset.bundleProduct === id);
      target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
      target?.querySelector<HTMLSelectElement>('select')?.focus({ preventScroll: true });
    }
  };
  return <BundleContext.Provider value={register}><BundleHotspotsContext.Provider value={hotspots}><div {...box} ref={root}>
    {children}
    <button className="xe-bundle-add" type="button" {...buyButton(locked, available)} onClick={() => { if (!locked) commerce?.addMany(choices); }}>{textProp(node.props, 'label', 'Add all to cart')}</button>
    {!available && editing && <p>Choose an available product for each item to enable the bundle.</p>}
  </div></BundleHotspotsContext.Provider></BundleContext.Provider>;
};
